import { z } from 'zod'
import { heureAParis, jourAParis } from './calendrier'
import { formaterJour } from './dates'
import type { StatutAccesTemporaire } from './domaine'
import { TransitionInvalide } from './machine'
import { type NotificationDefi, texteSansDonnee } from './notifications'

// Accès temporaire à un espace privé (invariant I4) : un administrateur n'entre dans l'espace
// privé d'une titulaire que si elle le lui accorde, en lecture seule, pour 48 h au plus,
// révocable à tout moment. Table des transitions en données et fonction pure ; le service
// (packages/db/src/services/acces-temporaires.ts) vérifie les droits, écrit, journalise et
// applique les effets (notifications). Le périmètre ne compte qu'un accès accordé dont la fin
// est dans le futur : l'échéance vaut d'elle-même, le statut `expire` ne fait que la constater.

/** Durée maximale d'un accès accordé. */
export const DUREE_MAX_ACCES_HEURES = 48

/** `aucun` : avant la demande (la ligne n'existe pas encore). */
export type EtatAcces = {
  statut: StatutAccesTemporaire | 'aucun'
  fin: Date | null
}

export type EvenementAcces =
  | { type: 'demander' }
  | { type: 'accorder'; dureeHeures: number; maintenant: Date }
  | { type: 'refuser' }
  | { type: 'revoquer' }
  | { type: 'expirer'; maintenant: Date }

/** notifier_titulaires : la demande arrive sur leurs appareils ; notifier_beneficiaire : la réponse. */
export type EffetAcces = 'notifier_titulaires' | 'notifier_beneficiaire'

export const TRANSITIONS_ACCES: readonly {
  evenement: EvenementAcces['type']
  depuis: readonly EtatAcces['statut'][]
  vers: StatutAccesTemporaire
  effets: readonly EffetAcces[]
}[] = [
  { evenement: 'demander', depuis: ['aucun'], vers: 'demande', effets: ['notifier_titulaires'] },
  {
    evenement: 'accorder',
    depuis: ['demande'],
    vers: 'accorde',
    effets: ['notifier_beneficiaire'],
  },
  { evenement: 'refuser', depuis: ['demande'], vers: 'refuse', effets: ['notifier_beneficiaire'] },
  { evenement: 'revoquer', depuis: ['accorde'], vers: 'revoque', effets: [] },
  { evenement: 'expirer', depuis: ['accorde'], vers: 'expire', effets: [] },
]

const echu = (fin: Date | null, maintenant: Date) =>
  fin !== null && fin.getTime() <= maintenant.getTime()

export function transitionAcces(
  etat: EtatAcces,
  evenement: EvenementAcces,
): { statut: StatutAccesTemporaire; fin: Date | null; effets: EffetAcces[] } {
  const ligne = TRANSITIONS_ACCES.find(
    (l) => l.evenement === evenement.type && l.depuis.includes(etat.statut),
  )
  if (!ligne) throw new TransitionInvalide(etat.statut, evenement.type)
  let fin = etat.fin
  if (evenement.type === 'accorder') {
    const h = evenement.dureeHeures
    if (!Number.isFinite(h) || h <= 0 || h > DUREE_MAX_ACCES_HEURES)
      throw new RangeError(`Un accès s’accorde pour ${DUREE_MAX_ACCES_HEURES} h au plus.`)
    fin = new Date(evenement.maintenant.getTime() + h * 3600_000)
  }
  if (evenement.type === 'expirer' && !echu(etat.fin, evenement.maintenant))
    throw new TransitionInvalide(etat.statut, evenement.type)
  return { statut: ligne.vers, fin, effets: [...ligne.effets] }
}

/** Statut à afficher : un accès accordé dont la fin est passée est expiré, même pas encore écrit. */
export function statutEffectifAcces(
  a: { statut: StatutAccesTemporaire; fin: Date | null },
  maintenant: Date,
): StatutAccesTemporaire {
  return a.statut === 'accorde' && echu(a.fin, maintenant) ? 'expire' : a.statut
}

// Saisies

export const demandeAcces = z.object({ motif: z.string().trim().min(1).max(500) }).strict()
export type DemandeAcces = z.infer<typeof demandeAcces>

export const accordAcces = z
  .object({ dureeHeures: z.number().int().min(1).max(DUREE_MAX_ACCES_HEURES) })
  .strict()
export type AccordAcces = z.infer<typeof accordAcces>

// Notifications (écran verrouillé) : qui, quel espace, jusqu'à quand ; jamais le motif.

const URL_ACCES = '/acces-temporaires'
const prenom = (nom: string) => texteSansDonnee(nom.split(' ')[0] ?? nom, 40) || 'Un administrateur'
const nomEspace = (espace: string) => texteSansDonnee(espace, 60) || 'votre espace'

export function notificationDemandeAcces(d: {
  id: string
  demandeur: string
  espace: string
}): NotificationDefi {
  return {
    titre: 'Demande d’accès à votre espace',
    corps: `${prenom(d.demandeur)} demande à consulter « ${nomEspace(d.espace)} » en lecture seule. À vous d’accepter ou de refuser.`,
    tag: `acces-${d.id}`,
    url: URL_ACCES,
    exigeInteraction: false,
    silencieuse: false,
  }
}

export function notificationAccesDecide(d: {
  id: string
  espace: string
  statut: 'accorde' | 'refuse'
  fin: Date | null
}): NotificationDefi {
  const espace = nomEspace(d.espace)
  return {
    titre: d.statut === 'accorde' ? 'Accès accordé' : 'Accès refusé',
    corps:
      d.statut === 'accorde' && d.fin
        ? `« ${espace} » vous est ouvert en lecture seule jusqu’au ${formaterJour(jourAParis(d.fin))} à ${heureAParis(d.fin)}.`
        : `Votre demande d’accès à « ${espace} » est refusée.`,
    tag: `acces-${d.id}`,
    url: URL_ACCES,
    exigeInteraction: false,
    silencieuse: false,
  }
}
